import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Save, AlertTriangle, Crosshair } from "lucide-react";
import MediaInput from "@/components/MediaInput";

/**
 * Phase 1 — Panorama 360 admin helpers.
 *
 * Three exports:
 *  - PanoramaAspectHint    Warn if the panorama image isn't ~2:1.
 *  - PanoramaSceneFields   Default view angle / category / parent unit picker.
 *  - PanoramaHotspotsEditor List + create + edit + delete hotspots for a scene,
 *    with a live mini-preview using Photo Sphere Viewer; clicking inside the
 *    preview snaps a new hotspot at the clicked yaw/pitch.
 */

export function PanoramaAspectHint({ url }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!url) { setInfo(null); return; }
    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      const ratio = img.naturalWidth / img.naturalHeight;
      setInfo({ w: img.naturalWidth, h: img.naturalHeight, ratio });
    };
    img.onerror = () => { if (!cancelled) setInfo({ error: true }); };
    img.src = url;
    return () => { cancelled = true; };
  }, [url]);
  if (!info) return null;
  if (info.error) return <div className="text-xs text-amber-700 mt-1.5 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Couldn’t inspect image — make sure it loads.</div>;
  const close = Math.abs(info.ratio - 2) < 0.05;
  if (close) return <div className="text-xs text-zinc-500 mt-1.5">{info.w}×{info.h} · 2:1 ✓ equirectangular</div>;
  return (
    <div className="text-xs text-amber-700 mt-1.5 inline-flex items-center gap-1.5" data-testid="panorama-aspect-warning">
      <AlertTriangle className="h-3.5 w-3.5" /> {info.w}×{info.h} (ratio {info.ratio.toFixed(2)}) — expected close to 2:1 (e.g. 6000×3000)
    </div>
  );
}

export function PanoramaSceneFields({ form, setForm, pid }) {
  const [units, setUnits] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/projects/${pid}/entities`).then((r) => {
      if (!cancelled) setUnits(r.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [pid]);
  return (
    <>
      <div className="grid grid-cols-3 gap-2" data-testid="panorama-scene-fields">
        <label className="block">
          <span className="text-xs text-zinc-600 mb-1 block">Initial yaw (°)</span>
          <input className="i" type="number" value={radToDeg(form.panorama_initial_yaw)}
            onChange={(e) => setForm({ ...form, panorama_initial_yaw: degToRad(parseFloat(e.target.value) || 0) })}
            data-testid="panorama-init-yaw" />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-600 mb-1 block">Initial pitch (°)</span>
          <input className="i" type="number" value={radToDeg(form.panorama_initial_pitch)}
            onChange={(e) => setForm({ ...form, panorama_initial_pitch: degToRad(parseFloat(e.target.value) || 0) })}
            data-testid="panorama-init-pitch" />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-600 mb-1 block">Initial FOV (°)</span>
          <input className="i" type="number" value={form.panorama_initial_fov ?? 60}
            onChange={(e) => setForm({ ...form, panorama_initial_fov: parseFloat(e.target.value) || 60 })}
            data-testid="panorama-init-fov" />
        </label>
      </div>
      <label className="block">
        <span className="text-xs text-zinc-600 mb-1 block">Category</span>
        <select className="i" value={form.panorama_category || "project"}
          onChange={(e) => setForm({ ...form, panorama_category: e.target.value })}
          data-testid="panorama-category">
          <option value="project">Project</option>
          <option value="unit">Unit (interior)</option>
          <option value="amenity">Amenity</option>
          <option value="view">View</option>
          <option value="custom">Custom</option>
        </select>
      </label>
      {form.panorama_category === "unit" && (
        <label className="block">
          <span className="text-xs text-zinc-600 mb-1 block">Linked unit (drives the 360 Tour tab in Unit Detail)</span>
          <select className="i" value={form.panorama_parent_unit_id || ""}
            onChange={(e) => setForm({ ...form, panorama_parent_unit_id: e.target.value || null })}
            data-testid="panorama-parent-unit">
            <option value="">— Select —</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.code}{u.name && u.name !== u.code ? ` — ${u.name}` : ""}</option>)}
          </select>
        </label>
      )}
      <label className="block">
        <span className="text-xs text-zinc-600 mb-1 block">Thumbnail (optional, used in 360 Tour selector)</span>
        <MediaInput projectId={pid} value={form.panorama_thumb_url || ""}
          onChange={(v) => setForm({ ...form, panorama_thumb_url: v })} accept="image/*" testid="panorama-thumb" />
      </label>
    </>
  );
}

export function PanoramaHotspotsEditor({ sceneId, projectId, scenes }) {
  const [hotspots, setHotspots] = useState([]);
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null); // null | hotspot-shape
  const [placeMode, setPlaceMode] = useState(false);
  const previewRef = useRef(null);
  const psvRef = useRef(null);
  const scene = useMemo(() => scenes.find((s) => s.id === sceneId), [scenes, sceneId]);

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/admin/scenes/${sceneId}/panorama-hotspots`);
      setHotspots(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, [sceneId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    api.get(`/admin/projects/${projectId}/entities`).then((r) => setUnits(r.data || [])).catch(() => {});
  }, [projectId]);

  // Build a tiny live PSV preview so the admin can click to place hotspots.
  useEffect(() => {
    if (!scene?.media_url || !previewRef.current) return;
    let viewer;
    (async () => {
      const { Viewer } = await import("@photo-sphere-viewer/core");
      const { MarkersPlugin } = await import("@photo-sphere-viewer/markers-plugin");
      await import("@photo-sphere-viewer/core/index.css");
      await import("@photo-sphere-viewer/markers-plugin/index.css");
      viewer = new Viewer({
        container: previewRef.current,
        panorama: scene.media_url,
        defaultYaw: scene.panorama_initial_yaw ?? 0,
        defaultPitch: scene.panorama_initial_pitch ?? 0,
        navbar: false,
        plugins: [[MarkersPlugin, { markers: [] }]],
      });
      psvRef.current = viewer;
      viewer.addEventListener("click", (e) => {
        const data = e.data;
        if (!data || data.rightclick) return;
        if (placeModeRef.current) {
          const yaw = data.yaw, pitch = data.pitch;
          placeModeRef.current = false;
          setPlaceMode(false);
          setEditing({
            project_id: projectId, panorama_scene_id: sceneId,
            label: "", description: "", action_type: "show_info", target_id: "",
            yaw, pitch, sort_order: hotspots.length, visibility: true,
          });
          toast.success(`Placed at yaw ${radToDeg(yaw).toFixed(0)}°, pitch ${radToDeg(pitch).toFixed(0)}°`);
        }
      });
    })();
    return () => { try { viewer?.destroy(); } catch (_) {} psvRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.media_url]);

  // PSV's click handler captures the closure at attach time, so use a ref
  // to read the latest placeMode value when the click actually fires.
  const placeModeRef = useRef(false);
  useEffect(() => { placeModeRef.current = placeMode; }, [placeMode]);

  // Refresh markers in the preview every time hotspots change.
  useEffect(() => {
    const viewer = psvRef.current;
    if (!viewer) return;
    (async () => {
      const { MarkersPlugin } = await import("@photo-sphere-viewer/markers-plugin");
      const plugin = viewer.getPlugin(MarkersPlugin);
      if (!plugin) return;
      try {
        plugin.setMarkers(hotspots.map((h) => ({
          id: h.id,
          position: { yaw: h.yaw || 0, pitch: h.pitch || 0 },
          html: `<div style="width:18px;height:18px;border-radius:99px;background:#C4A47C;box-shadow:0 0 0 2px rgba(0,0,0,.5),0 0 12px #C4A47C"></div>`,
          anchor: "center center",
          tooltip: h.label || h.action_type,
        })));
      } catch (_) {}
    })();
  }, [hotspots]);

  const save = async (h) => {
    try {
      if (h.id) await api.put(`/admin/panorama-hotspots/${h.id}`, h);
      else await api.post(`/admin/panorama-hotspots`, h);
      toast.success("Saved");
      setEditing(null);
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this hotspot?")) return;
    await api.delete(`/admin/panorama-hotspots/${id}`);
    reload();
  };

  const otherPanoramaScenes = scenes.filter((s) => s.id !== sceneId && s.type === "panorama_360");
  const regularScenes = scenes.filter((s) => s.type !== "panorama_360");

  return (
    <div className="mt-5 border-t border-zinc-200 pt-4" data-testid="panorama-hotspots-editor">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">Hotspots</h4>
          <p className="text-xs text-zinc-500 mt-0.5">Click inside the preview to place, or add manually.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPlaceMode((v) => !v)}
            className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-1.5 ${placeMode ? "border-amber-500 bg-amber-50 text-amber-800" : "border-zinc-300 bg-white hover:bg-zinc-50"}`}
            data-testid="hotspot-place-toggle"
          >
            <Crosshair className="h-3.5 w-3.5" /> {placeMode ? "Click panorama…" : "Click to place"}
          </button>
          <button
            type="button"
            onClick={() => setEditing({
              project_id: projectId, panorama_scene_id: sceneId,
              label: "", description: "", action_type: "show_info", target_id: "",
              yaw: 0, pitch: 0, sort_order: hotspots.length, visibility: true,
            })}
            className="text-xs px-3 py-1.5 rounded-md bg-zinc-900 text-white inline-flex items-center gap-1.5"
            data-testid="hotspot-add-manual"
          >
            <Plus className="h-3.5 w-3.5" /> Add manually
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-3 mt-3">
        <div ref={previewRef} data-testid="panorama-preview"
             className="h-64 rounded-lg bg-black overflow-hidden ring-1 ring-zinc-200" />
        <div className="max-h-64 overflow-auto scrollbar-thin">
          {hotspots.length === 0 && (
            <div className="text-xs text-zinc-500 px-3 py-6 text-center" data-testid="hotspots-empty">No hotspots yet.</div>
          )}
          {hotspots.map((h) => (
            <div key={h.id} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 text-sm" data-testid={`hotspot-row-${h.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{h.label || <span className="text-zinc-400">Untitled</span>}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {h.action_type} · yaw {radToDeg(h.yaw).toFixed(0)}° / pitch {radToDeg(h.pitch).toFixed(0)}°
                </div>
              </div>
              <button onClick={() => setEditing({ ...h })} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`hotspot-edit-${h.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => del(h.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`hotspot-delete-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <HotspotForm
          form={editing}
          setForm={setEditing}
          panoramaScenes={otherPanoramaScenes}
          regularScenes={regularScenes}
          units={units}
          onCancel={() => setEditing(null)}
          onSave={() => save(editing)}
        />
      )}
    </div>
  );
}

function HotspotForm({ form, setForm, panoramaScenes, regularScenes, units, onCancel, onSave }) {
  const targetOptions = useMemo(() => {
    if (form.action_type === "go_to_panorama") return panoramaScenes.map((s) => ({ id: s.id, label: s.name }));
    if (form.action_type === "go_to_scene") return regularScenes.map((s) => ({ id: s.id, label: s.name }));
    if (form.action_type === "open_unit") return units.map((u) => ({ id: u.id, label: u.code + (u.name && u.name !== u.code ? ` — ${u.name}` : "") }));
    return [];
  }, [form.action_type, panoramaScenes, regularScenes, units]);
  const needsTarget = form.action_type !== "show_info";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={onCancel} data-testid="hotspot-form-modal">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">{form.id ? "Edit hotspot" : "New hotspot"}</h3>
          <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-900"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Label</span>
            <input className="i" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="hotspot-label" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-zinc-600 mb-1 block">Yaw (°)</span>
              <input className="i" type="number" value={radToDeg(form.yaw).toFixed(2)}
                onChange={(e) => setForm({ ...form, yaw: degToRad(parseFloat(e.target.value) || 0) })}
                data-testid="hotspot-yaw" />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-600 mb-1 block">Pitch (°)</span>
              <input className="i" type="number" value={radToDeg(form.pitch).toFixed(2)}
                onChange={(e) => setForm({ ...form, pitch: degToRad(parseFloat(e.target.value) || 0) })}
                data-testid="hotspot-pitch" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Action</span>
            <select className="i" value={form.action_type} onChange={(e) => setForm({ ...form, action_type: e.target.value, target_id: "" })}
              data-testid="hotspot-action-type">
              <option value="show_info">Show info</option>
              <option value="go_to_panorama">Go to another panorama</option>
              <option value="go_to_scene">Go to a regular scene</option>
              <option value="open_unit">Open unit detail</option>
            </select>
          </label>
          {needsTarget && (
            <label className="block">
              <span className="text-xs text-zinc-600 mb-1 block">Target</span>
              <select className="i" value={form.target_id || ""} onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                data-testid="hotspot-target-id">
                <option value="">Select…</option>
                {targetOptions.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
          )}
          <label className="block">
            <span className="text-xs text-zinc-600 mb-1 block">Description (optional)</span>
            <textarea className="i h-16" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="hotspot-description" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50">Cancel</button>
          <button onClick={onSave} className="px-4 py-2 rounded-md text-sm bg-zinc-900 text-white inline-flex items-center gap-1.5" data-testid="hotspot-save">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

const radToDeg = (r) => ((r || 0) * 180 / Math.PI);
const degToRad = (d) => ((d || 0) * Math.PI / 180);
